import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  BarChart3, 
  TrendingUp, 
  Target, 
  Heart, 
  Brain, 
  Sparkles, 
  Award,
  Activity,
  Download,
  Share2,
  RefreshCw,
  Clock,
  Sun,
  Moon,
  Star,
  Zap,
  Calendar,
  MapPin,
  User,
  Eye,
  BookOpen,
  Lightbulb,
  FileText,
  PieChart,
  Users,
  Globe,
  Shield,
  Gift
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { userJourneyAPI } from '../services/userJourneyService'
import toast from 'react-hot-toast'

interface AstrologicalReport {
  userId: string
  generatedAt: string
  period: string
  astrologicalProfile: {
    sunSign: string
    moonSign: string
    ascendant: string
    nakshatra: string
    currentDasha: string
    planetaryPositions: Array<{ planet: string; sign: string; house: number; status: string }>
    importantAspects: Array<{ planet1: string; planet2: string; aspect: string; influence: string }>
    currentTransits: Array<{ planet: string; transit: string; impact: string }>
    favorablePeriods: Array<{ period: string; reason: string; recommendations: string[] }>
  }
  personalityInsights: {
    coreTraits: string[]
    strengths: string[]
    challenges: string[]
    lifePurpose: string
    careerAptitudes: string[]
    relationshipPatterns: string[]
    spiritualPath: string
  }
  lifeAnalysis: {
    careerGuidance: {
      currentPhase: string
      opportunities: string[]
      challenges: string[]
      recommendations: string[]
      timing: string
    }
    relationshipInsights: {
      currentPhase: string
      compatibilityFactors: string[]
      timing: string
      recommendations: string[]
    }
    healthGuidance: {
      constitution: string
      favorablePractices: string[]
      precautions: string[]
      timing: string
    }
    spiritualGrowth: {
      currentPhase: string
      practices: string[]
      milestones: string[]
      guidance: string
    }
  }
  predictiveInsights: {
    shortTerm: Array<{ period: string; prediction: string; guidance: string }>
    mediumTerm: Array<{ period: string; prediction: string; guidance: string }>
    longTerm: Array<{ period: string; prediction: string; guidance: string }>
    favorableDates: Array<{ date: string; activity: string; reason: string }>
    challengingDates: Array<{ date: string; precaution: string; reason: string }>
  }
  personalizedRecommendations: {
    dailyPractices: Array<{ practice: string; timing: string; duration: string; astrologicalReason: string }>
    weeklyRituals: Array<{ ritual: string; day: string; purpose: string; method: string }>
    monthlyFocus: string
    gemstoneRecommendations: Array<{ gemstone: string; purpose: string; wearingMethod: string }>
    mantraSuggestions: Array<{ mantra: string; purpose: string; timing: string; method: string }>
  }
  chartAnalysis: {
    houseAnalysis: Array<{ house: number; sign: string; planets: string[]; significance: string }>
    planetaryStrengths: Array<{ planet: string; strength: string; influence: string }>
    doshas: Array<{ dosha: string; severity: string; remedies: string[] }>
    yogas: Array<{ yoga: string; type: string; influence: string; recommendations: string[] }>
  }
}

const Reports = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<AstrologicalReport | null>(null)
  const [activeSection, setActiveSection] = useState<'overview' | 'profile' | 'analysis' | 'predictions' | 'recommendations' | 'charts'>('overview')
  const [reportType, setReportType] = useState<'comprehensive' | 'astrological' | 'progress' | 'insights'>('comprehensive')

  const reportTypes = [
    { id: 'comprehensive', label: 'Comprehensive Report', icon: FileText, description: 'Complete astrological analysis with all insights' },
    { id: 'astrological', label: 'Astrological Profile', icon: Sun, description: 'Detailed birth chart and planetary analysis' },
    { id: 'progress', label: 'Progress Report', icon: TrendingUp, description: 'Your astrological journey and growth insights' },
    { id: 'insights', label: 'Personal Insights', icon: Lightbulb, description: 'Deep personality and life path analysis' }
  ]

  const sections = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'profile', label: 'Astrological Profile', icon: Sun },
    { id: 'analysis', label: 'Life Analysis', icon: Target },
    { id: 'predictions', label: 'Predictions', icon: Eye },
    { id: 'recommendations', label: 'Recommendations', icon: Sparkles },
    { id: 'charts', label: 'Chart Analysis', icon: PieChart }
  ]

  const generateReport = async () => {
    if (!user?.uid) {
      toast.error('Please log in to generate reports')
      return
    }

    setLoading(true)
    try {
      const response = await userJourneyAPI.generateReport(user.uid, reportType)
      
      if (response.success) {
        setReport(response.report)
        toast.success('Report generated successfully!')
      } else {
        toast.error('Failed to generate report')
      }
    } catch (error) {
      console.error('Error generating report:', error)
      toast.error('Failed to generate report')
    } finally {
      setLoading(false)
    }
  }

  const downloadReport = () => {
    if (!report) return
    
    const reportData = JSON.stringify(report, null, 2)
    const blob = new Blob([reportData], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `astrological-report-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success('Report downloaded successfully!')
  }

  const shareReport = () => {
    if (!report) return
    
    if (navigator.share) {
      navigator.share({
        title: 'My Astrological Report',
        text: 'Check out my comprehensive astrological analysis!',
        url: window.location.href
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
      toast.success('Report link copied to clipboard!')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-3">
            <FileText className="text-yellow-400" />
            Astrological Reports
          </h1>
          <p className="text-xl text-purple-200 max-w-2xl mx-auto">
            Discover your cosmic blueprint with comprehensive astrological analysis, personalized insights, and predictive guidance
          </p>
        </motion.div>

        {/* Report Type Selection */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-semibold mb-4 text-center">Choose Report Type</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {reportTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setReportType(type.id as any)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  reportType === type.id
                    ? 'border-yellow-400 bg-yellow-400/10'
                    : 'border-purple-600 bg-purple-600/10 hover:border-purple-400'
                }`}
              >
                <type.icon className="text-2xl mb-2 mx-auto text-yellow-400" />
                <h3 className="font-semibold mb-1">{type.label}</h3>
                <p className="text-sm text-purple-200">{type.description}</p>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Generate Report Button */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <button
            onClick={generateReport}
            disabled={loading}
            className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-8 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 mx-auto disabled:opacity-50"
          >
            {loading ? (
              <>
                <RefreshCw className="animate-spin" />
                Generating Report...
              </>
            ) : (
              <>
                <Sparkles />
                Generate {reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report
              </>
            )}
          </button>
        </motion.div>

        {/* Report Display */}
        {report && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-6"
          >
            {/* Report Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">Your Astrological Report</h2>
                <p className="text-purple-200">
                  Generated on {new Date(report.generatedAt).toLocaleDateString()} | 
                  Period: {report.period}
                </p>
              </div>
              <div className="flex gap-2 mt-4 md:mt-0">
                <button
                  onClick={downloadReport}
                  className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg flex items-center gap-2"
                >
                  <Download />
                  Download
                </button>
                <button
                  onClick={shareReport}
                  className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg flex items-center gap-2"
                >
                  <Share2 />
                  Share
                </button>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {sections.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveSection(id as any)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeSection === id
                      ? 'bg-yellow-500 text-black'
                      : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>

            {/* Report Content */}
            <div className="space-y-6">
              {/* Overview Section */}
              {activeSection === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                    <Sun className="text-yellow-400 text-3xl mx-auto mb-2" />
                    <h3 className="font-semibold mb-1">Sun Sign</h3>
                    <p className="text-2xl font-bold text-yellow-300">{report.astrologicalProfile.sunSign}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                    <Moon className="text-blue-400 text-3xl mx-auto mb-2" />
                    <h3 className="font-semibold mb-1">Moon Sign</h3>
                    <p className="text-2xl font-bold text-blue-300">{report.astrologicalProfile.moonSign}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                    <Star className="text-purple-400 text-3xl mx-auto mb-2" />
                    <h3 className="font-semibold mb-1">Ascendant</h3>
                    <p className="text-2xl font-bold text-purple-300">{report.astrologicalProfile.ascendant}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                    <Zap className="text-green-400 text-3xl mx-auto mb-2" />
                    <h3 className="font-semibold mb-1">Current Dasha</h3>
                    <p className="text-2xl font-bold text-green-300">{report.astrologicalProfile.currentDasha}</p>
                  </div>
                </div>
              )}

              {/* Astrological Profile Section */}
              {activeSection === 'profile' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Planetary Positions</h3>
                      <div className="space-y-2">
                        {report.astrologicalProfile.planetaryPositions.map((pos, index) => (
                          <div key={index} className="bg-white/5 p-3 rounded-lg">
                            <span className="font-semibold">{pos.planet}:</span> {pos.sign} (House {pos.house}) - {pos.status}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Important Aspects</h3>
                      <div className="space-y-2">
                        {report.astrologicalProfile.importantAspects.map((aspect, index) => (
                          <div key={index} className="bg-white/5 p-3 rounded-lg">
                            <span className="font-semibold">{aspect.planet1} - {aspect.planet2}:</span> {aspect.aspect} - {aspect.influence}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Current Transits</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {report.astrologicalProfile.currentTransits.map((transit, index) => (
                        <div key={index} className="bg-white/5 p-4 rounded-lg">
                          <span className="font-semibold">{transit.planet}:</span> {transit.transit} - {transit.impact}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Life Analysis Section */}
              {activeSection === 'analysis' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Target className="text-blue-400" />
                      Career Guidance
                    </h3>
                    <div className="bg-white/5 p-4 rounded-lg space-y-2">
                      <p><span className="font-semibold">Current Phase:</span> {report.lifeAnalysis.careerGuidance.currentPhase}</p>
                      <p><span className="font-semibold">Opportunities:</span> {report.lifeAnalysis.careerGuidance.opportunities.join(', ')}</p>
                      <p><span className="font-semibold">Challenges:</span> {report.lifeAnalysis.careerGuidance.challenges.join(', ')}</p>
                      <p><span className="font-semibold">Timing:</span> {report.lifeAnalysis.careerGuidance.timing}</p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Heart className="text-pink-400" />
                      Relationship Insights
                    </h3>
                    <div className="bg-white/5 p-4 rounded-lg space-y-2">
                      <p><span className="font-semibold">Current Phase:</span> {report.lifeAnalysis.relationshipInsights.currentPhase}</p>
                      <p><span className="font-semibold">Compatibility Factors:</span> {report.lifeAnalysis.relationshipInsights.compatibilityFactors.join(', ')}</p>
                      <p><span className="font-semibold">Timing:</span> {report.lifeAnalysis.relationshipInsights.timing}</p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Shield className="text-green-400" />
                      Health Guidance
                    </h3>
                    <div className="bg-white/5 p-4 rounded-lg space-y-2">
                      <p><span className="font-semibold">Constitution:</span> {report.lifeAnalysis.healthGuidance.constitution}</p>
                      <p><span className="font-semibold">Favorable Practices:</span> {report.lifeAnalysis.healthGuidance.favorablePractices.join(', ')}</p>
                      <p><span className="font-semibold">Timing:</span> {report.lifeAnalysis.healthGuidance.timing}</p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Globe className="text-purple-400" />
                      Spiritual Growth
                    </h3>
                    <div className="bg-white/5 p-4 rounded-lg space-y-2">
                      <p><span className="font-semibold">Current Phase:</span> {report.lifeAnalysis.spiritualGrowth.currentPhase}</p>
                      <p><span className="font-semibold">Practices:</span> {report.lifeAnalysis.spiritualGrowth.practices.join(', ')}</p>
                      <p><span className="font-semibold">Guidance:</span> {report.lifeAnalysis.spiritualGrowth.guidance}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Predictions Section */}
              {activeSection === 'predictions' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Short-Term</h3>
                      <div className="space-y-2">
                        {report.predictiveInsights.shortTerm.map((prediction, index) => (
                          <div key={index} className="bg-white/5 p-3 rounded-lg">
                            <p className="font-semibold">{prediction.period}</p>
                            <p className="text-sm">{prediction.prediction}</p>
                            <p className="text-xs text-purple-200">{prediction.guidance}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Medium-Term</h3>
                      <div className="space-y-2">
                        {report.predictiveInsights.mediumTerm.map((prediction, index) => (
                          <div key={index} className="bg-white/5 p-3 rounded-lg">
                            <p className="font-semibold">{prediction.period}</p>
                            <p className="text-sm">{prediction.prediction}</p>
                            <p className="text-xs text-purple-200">{prediction.guidance}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Long-Term</h3>
                      <div className="space-y-2">
                        {report.predictiveInsights.longTerm.map((prediction, index) => (
                          <div key={index} className="bg-white/5 p-3 rounded-lg">
                            <p className="font-semibold">{prediction.period}</p>
                            <p className="text-sm">{prediction.prediction}</p>
                            <p className="text-xs text-purple-200">{prediction.guidance}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-green-400">Favorable Dates</h3>
                      <div className="space-y-2">
                        {report.predictiveInsights.favorableDates.map((date, index) => (
                          <div key={index} className="bg-green-600/20 p-3 rounded-lg">
                            <p className="font-semibold">{date.date}</p>
                            <p className="text-sm">{date.activity}</p>
                            <p className="text-xs">{date.reason}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-red-400">Challenging Dates</h3>
                      <div className="space-y-2">
                        {report.predictiveInsights.challengingDates.map((date, index) => (
                          <div key={index} className="bg-red-600/20 p-3 rounded-lg">
                            <p className="font-semibold">{date.date}</p>
                            <p className="text-sm">{date.precaution}</p>
                            <p className="text-xs">{date.reason}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recommendations Section */}
              {activeSection === 'recommendations' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Daily Practices</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {report.personalizedRecommendations.dailyPractices.map((practice, index) => (
                        <div key={index} className="bg-white/5 p-4 rounded-lg">
                          <h4 className="font-semibold mb-2">{practice.practice}</h4>
                          <p className="text-sm mb-1">Timing: {practice.timing} ({practice.duration})</p>
                          <p className="text-xs text-purple-200">{practice.astrologicalReason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Weekly Rituals</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {report.personalizedRecommendations.weeklyRituals.map((ritual, index) => (
                        <div key={index} className="bg-white/5 p-4 rounded-lg">
                          <h4 className="font-semibold mb-2">{ritual.ritual}</h4>
                          <p className="text-sm mb-1">{ritual.day} - {ritual.purpose}</p>
                          <p className="text-xs text-purple-200">{ritual.method}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Gemstone Recommendations</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {report.personalizedRecommendations.gemstoneRecommendations.map((gem, index) => (
                        <div key={index} className="bg-white/5 p-4 rounded-lg">
                          <h4 className="font-semibold mb-2">{gem.gemstone}</h4>
                          <p className="text-sm mb-1">{gem.purpose}</p>
                          <p className="text-xs text-purple-200">{gem.wearingMethod}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Mantra Suggestions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {report.personalizedRecommendations.mantraSuggestions.map((mantra, index) => (
                        <div key={index} className="bg-white/5 p-4 rounded-lg">
                          <h4 className="font-semibold mb-2">{mantra.mantra}</h4>
                          <p className="text-sm mb-1">{mantra.purpose} ({mantra.timing})</p>
                          <p className="text-xs text-purple-200">{mantra.method}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Chart Analysis Section */}
              {activeSection === 'charts' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">House Analysis</h3>
                      <div className="space-y-2">
                        {report.chartAnalysis.houseAnalysis.map((house, index) => (
                          <div key={index} className="bg-white/5 p-3 rounded-lg">
                            <p className="font-semibold">House {house.house}: {house.sign}</p>
                            <p className="text-sm">Planets: {house.planets.join(', ')}</p>
                            <p className="text-xs text-purple-200">{house.significance}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Planetary Strengths</h3>
                      <div className="space-y-2">
                        {report.chartAnalysis.planetaryStrengths.map((strength, index) => (
                          <div key={index} className="bg-white/5 p-3 rounded-lg">
                            <p className="font-semibold">{strength.planet}</p>
                            <p className="text-sm">{strength.strength}</p>
                            <p className="text-xs text-purple-200">{strength.influence}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Doshas</h3>
                      <div className="space-y-2">
                        {report.chartAnalysis.doshas.map((dosha, index) => (
                          <div key={index} className="bg-white/5 p-3 rounded-lg">
                            <p className="font-semibold">{dosha.dosha} - {dosha.severity}</p>
                            <p className="text-xs text-purple-200">Remedies: {dosha.remedies.join(', ')}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Yogas</h3>
                      <div className="space-y-2">
                        {report.chartAnalysis.yogas.map((yoga, index) => (
                          <div key={index} className="bg-white/5 p-3 rounded-lg">
                            <p className="font-semibold">{yoga.yoga} ({yoga.type})</p>
                            <p className="text-sm">{yoga.influence}</p>
                            <p className="text-xs text-purple-200">Recommendations: {yoga.recommendations.join(', ')}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* No Report State */}
        {!report && !loading && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <Gift className="text-6xl mx-auto mb-4 text-yellow-400" />
            <h3 className="text-2xl font-semibold mb-2">Ready to Discover Your Cosmic Blueprint?</h3>
            <p className="text-purple-200 max-w-md mx-auto">
              Generate a comprehensive astrological report to unlock insights about your personality, life path, and future possibilities.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default Reports 